import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { ApiWithLoadingService } from './api-with-loading.service';

import {
  Availability,
  Gather,
  JobPosting,
  JobSearchStatus, JobSearchStatusEnum,
  PersonPortfolioUrl
} from '../gather-typings';

import { HttpHeaders, HttpParams } from '@angular/common/http';
import { ClientVMSSubInfo, TalentMoatsTravel } from "../../../typings";

export interface UpdateInterestData {
  statusId: number[];
  isTalentLooking: boolean;
  checkInDate: Date;
  jobSearchStatus: JobSearchStatus;
  numWeeksNotice: number;
  fromTextMessage: boolean;
  notes?: string;
  availability?: Availability;
}

export interface ErrorResponse {
  error: string;
  errorId: string;
  errorDescription: string;
}

export function isErrorResponse(obj: any): obj is ErrorResponse {
  return typeof obj === 'object' && obj !== null && 'error' in obj;
}

//TODO cleanup file at end of project

@Injectable({
  providedIn: 'root'
})
export class GatherApiService {
  constructor(private apiService: ApiWithLoadingService) {}

  isTalentStatusDNU(obfuscatedTalentId: string, path: string): Observable<boolean> {
    return this.apiService.httpWrapper<boolean>({
      method: 'GET',
      url: `/api/reengage/${obfuscatedTalentId}/isTalentDNU/${path}/`,
      authenticated: true
    });
  }

  saveAvailabilityMoats(
    trackingId: string,
    availability: Availability,
    saveAvailability: boolean
  ): Observable<void> {
    return this.apiService.httpWrapper<void>({
      method: 'POST',
      url: `/api/gather/availability/${trackingId}/interested/${saveAvailability}`,
      body: availability,
      authenticated: true
    });
  }

  getJobPostingForGather(trackingId: string): Observable<JobPosting> {
    return this.apiService.httpWrapper<JobPosting>({
      method: 'GET',
      url: `/api/gather/jobposting/${trackingId}`,
      authenticated: true
    });
  }

  getMoatsAvailability(trackingId: string): Observable<Availability> {
    return this.apiService.httpWrapper<Availability>({
      method: 'GET',
      url: `/api/gather/availability/${trackingId}`,
      authenticated: true
    }, true);
  }

  getMoatsTravel(trackingId: string): Observable<TalentMoatsTravel> {
    return this.apiService.httpWrapper<TalentMoatsTravel>({
      method: 'GET',
      url: `/api/gather/travel/${trackingId}`,
      authenticated: true
    }, true);
  }

  saveMoatsTravel(trackingId: string, travel: TalentMoatsTravel): Observable<void> {
    return this.apiService.httpWrapper<void>({
      method: 'POST',
      url: `/api/gather/travel/${trackingId}`,
      body: travel,
      authenticated: true
    });
  }

  isGatherIdValid(trackingId: string): Observable<boolean> {
    return this.apiService.httpWrapper({
      method: 'GET',
      url: `/api/gather/token/${trackingId}/isValid`
    });
  }

  getGatherByToken(trackingId: string): Observable<Gather> {
    return this.apiService.httpWrapper<Gather>({
      method: 'GET',
      url: `/api/gather/token/${trackingId}`,
      authenticated: true
    });
  }

  getTalentPersonProfileUrls(id: number, skipLoading = false): Observable<PersonPortfolioUrl[]> {
    return this.apiService.httpWrapper<PersonPortfolioUrl[]>(
      {
        method: 'GET',
        url: `/api/gather/talent/${id}/portfolioUrls`,
        authenticated: true
      },
      skipLoading
    );
  }

  deletePersonUri(id: number): Observable<void> {
    return this.apiService.httpWrapper<null>(
      {
        method: 'DELETE',
        url: `/api/gather/personUri/${id}`
      },
      true
    );
  }

  savePersonURI(personPortfolioUrl: PersonPortfolioUrl): Observable<number> {
    return this.apiService.httpWrapper<number>(
      {
        method: 'POST',
        url: `/api/gather/personUri`,
        body: personPortfolioUrl,
        authenticated: true
      },
      true
    );
  }

  updatePersonURI(personPortfolioUrl: PersonPortfolioUrl): Observable<number> {
    return this.apiService.httpWrapper<number>(
      {
        method: 'POST',
        url: `/api/gather/personUri/${personPortfolioUrl.personUriId}`,
        body: personPortfolioUrl,
        authenticated: true
      },
      true
    );
  }

  verifyPersonUri(uri: string): Observable<number> {
    return this.apiService.httpWrapper(
      {
        method: 'POST',
        url: `/api/gather/talent/verifyURI`,
        body: uri,
        authenticated: true
      },
      true
    );
  }

  getTalentStatusForOrder(orderId: number, personId: number): Observable<number> {
    return this.apiService.httpWrapper({
      method: 'GET',
      url: `/api/gather/talentstatus/${orderId}/${personId}`
    });
  }

  updateGatherResponseNotInterested(trackingId: string, fromTextMessage: boolean): Observable<void> {
    const params = new HttpParams().set('fromTextMessage', String(fromTextMessage));
    return this.apiService.httpWrapper({
      method: 'POST',
      url: `/api/gather/availability/${trackingId}/track-not-interested`,
      params
    }) as Observable<void>;
  }

  saveAsNotInterested(
    trackingId: string,
    statusId: number[],
    notes: string,
    jobSearchStatus: string,
    availability: Availability,
    fromTextMessage: boolean
  ): Observable<void> {
    const params = new HttpParams()
      .set('statusIds', String(statusId))
      .set('isTalentLooking', String(jobSearchStatus !== JobSearchStatusEnum.NOT_LOOKING))
      .set('jobSearchStatus', jobSearchStatus)
      .set('numWeeksNotice', String(0)) // defaulting to 0 since not applicable here
      .set('fromTextMessage', String(fromTextMessage));
    return this.apiService.httpWrapper({
      method: 'POST',
      url: `/api/gather/availability/${trackingId}/not-interested`,
      body: { notes, availability },
      params: params,
      authenticated: true
    }) as Observable<void>;
  }

  updateGatherResponseInterested(trackingId: string, fromTextMessage: boolean): Observable<void> {
    const params = new HttpParams().set('fromTextMessage', String(fromTextMessage));
    return this.apiService.httpWrapper({
      method: 'POST',
      url: `/api/gather/availability/${trackingId}/track-interested`,
      params
    }) as Observable<void>;
  }

  saveResumeDetail(resumeDetail: ResumeDetail): Observable<ResumeDetail> {
    return this.apiService.httpWrapper<ResumeDetail>( {
      method: 'POST',
      url: `/api/resume/save-resume`,
      body: resumeDetail
    });
  }

  getClientVMSSubInfo(orderId: Number) : Observable<ClientVMSSubInfo> {
    return this.apiService.httpWrapper<any>({
      method: 'GET',
      url: `/api/vms-submittal/client/${orderId}`,
      authenticated: false
    });
  }
}
